<template>
  <v-container fluid class="warehouse-exit-list-page pa-0">
    <v-card :loading="loading" outlined class="warehouse-exit-list-card">
      <div class="warehouse-exit-header">
        <div class="warehouse-exit-title-wrap">
          <v-btn icon small class="mr-2" @click="$router.go(-1)">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <div>
            <div class="page-title">Warehouse Exit List</div>
            <div class="page-subtitle">
              Monitor invoices waiting at the door and review completed scans
            </div>
          </div>
        </div>

        <v-btn small rounded outlined color="primary" :loading="loading" @click="load">
          <v-icon left small>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </div>

      <div class="warehouse-exit-filter-bar">
        <v-text-field
          v-model="search"
          outlined
          prepend-inner-icon="search"
          dense
          :label="view === 'pending' ? 'Search invoice, customer' : 'Search invoice, guard'"
          single-line
          hide-details
          clearable
          class="warehouse-exit-search"
        />

        <v-spacer />

        <div v-if="view === 'history'" class="history-date-filter">
          <v-menu v-model="startMenu" offset-y :close-on-content-click="false" min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field v-model="historyStart" dense outlined readonly hide-details label="From" prepend-inner-icon="mdi-calendar" v-bind="attrs" v-on="on" />
            </template>
            <v-date-picker v-model="historyStart" :max="historyEnd" @input="startMenu = false" />
          </v-menu>
          <v-menu v-model="endMenu" offset-y :close-on-content-click="false" min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field v-model="historyEnd" dense outlined readonly hide-details label="To" prepend-inner-icon="mdi-calendar" v-bind="attrs" v-on="on" />
            </template>
            <v-date-picker v-model="historyEnd" :min="historyStart" :max="today" @input="endMenu = false" />
          </v-menu>
          <v-btn small rounded color="primary" :loading="loading" @click="load">Apply</v-btn>
        </div>

        <v-btn-toggle v-model="view" mandatory dense color="primary" class="view-toggle">
          <v-btn small value="pending">
            <v-icon left small>mdi-clock-outline</v-icon>
            Waiting
            <v-chip x-small class="ml-2">{{ dashboard.pendingCount || 0 }}</v-chip>
          </v-btn>
          <v-btn small value="history">
            <v-icon left small>mdi-history</v-icon>
            Scan History
          </v-btn>
        </v-btn-toggle>
      </div>

      <v-card-text class="pa-4">
        <div class="summary-row mb-4">
          <div class="summary-box">
            <div class="summary-label">Waiting for Scan</div>
            <div class="summary-value orange--text text--darken-3">{{ dashboard.pendingCount || 0 }}</div>
          </div>
          <div class="summary-box">
            <div class="summary-label">Scans Today</div>
            <div class="summary-value green--text text--darken-3">{{ dashboard.scansToday || 0 }}</div>
          </div>
          <div class="summary-box">
            <div class="summary-label">Mismatches Today</div>
            <div class="summary-value red--text text--darken-3">{{ dashboard.mismatchesToday || 0 }}</div>
          </div>
        </div>

        <div v-if="view === 'pending'" class="list-section-header">
          <div>
            <div class="section-title">Printed Invoices Waiting for Scan</div>
            <div class="section-subtitle">Invoices with an exit QR that have not been cleared by a guard.</div>
          </div>
        </div>

        <v-data-table
          v-if="view === 'pending'"
          :mobile-breakpoint="0"
          :headers="pendingHeaders"
          :items="dashboard.pendingInvoices || []"
          :search="search"
          :loading="loading"
          :items-per-page="15"
          sort-by="exitPrintedAt"
          sort-desc
          class="warehouse-exit-table"
        >
          <template v-slot:item="{ item }">
            <tr class="warehouse-exit-row">
              <td class="text-left font-weight-bold primary--text">#{{ item.localId }}</td>
              <td class="text-left">{{ item.Customer ? item.Customer.name : "Unknown Customer" }}</td>
              <td class="text-right font-weight-bold">{{ money(item.exitPrintedTotal) }}</td>
              <td class="text-right" :class="amountChanged(item) ? 'red--text font-weight-bold' : 'font-weight-bold'">{{ money(item.total) }}</td>
              <td class="text-left">{{ dateTime(item.exitPrintedAt) }}</td>
              <td class="text-left">
                <v-chip
                  small
                  class="font-weight-bold"
                  :color="amountChanged(item) ? 'red lighten-5' : 'orange lighten-5'"
                  :text-color="amountChanged(item) ? 'red darken-3' : 'orange darken-3'"
                >
                  {{ amountChanged(item) ? "Total Changed" : "Waiting" }}
                </v-chip>
              </td>
              <td class="text-right">
                <v-btn icon small color="red" @click="cancelExit(item)">
                  <v-icon small>mdi-close-circle-outline</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>

          <v-alert slot="no-results" :value="true" color="error" icon="warning">
            Your search for "{{ search }}" found no results.
          </v-alert>

          <template v-slot:no-data>
            <div class="empty-state">
              <v-icon size="48" color="success">mdi-shield-check-outline</v-icon>
              <div class="empty-title">Every printed invoice has been cleared</div>
              <div class="empty-subtitle">There are no invoices currently waiting for a guard scan.</div>
            </div>
          </template>
        </v-data-table>

        <div v-else class="list-section-header">
          <div>
            <div class="section-title">Exit Scan History</div>
            <div class="section-subtitle">Approved exits, repeat scans, and invoice-total mismatches.</div>
          </div>
        </div>

        <v-data-table
          v-if="view === 'history'"
          :mobile-breakpoint="0"
          :headers="historyHeaders"
          :items="dashboard.recent || []"
          :search="search"
          :loading="loading"
          :items-per-page="25"
          sort-by="scannedAt"
          sort-desc
          class="warehouse-exit-table"
        >
          <template v-slot:item="{ item }">
            <tr class="warehouse-exit-row">
              <td class="text-left font-weight-bold primary--text">#{{ item.Invoice && item.Invoice.localId }}</td>
              <td class="text-left">{{ item.Invoice && item.Invoice.Customer ? item.Invoice.Customer.name : "Unknown Customer" }}</td>
              <td class="text-left font-weight-bold">{{ item.InvoiceExitGuard && item.InvoiceExitGuard.name }}</td>
              <td class="text-right font-weight-bold">{{ money(item.Invoice && item.Invoice.total) }}</td>
              <td class="text-left">
                <v-chip small class="font-weight-bold" :color="resultColor(item.result)" :text-color="resultTextColor(item.result)">
                  {{ resultLabel(item.result) }}
                </v-chip>
              </td>
              <td class="text-left">{{ dateTime(item.scannedAt) }}</td>
            </tr>
          </template>

          <v-alert slot="no-results" :value="true" color="error" icon="warning">
            Your search for "{{ search }}" found no results.
          </v-alert>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import service from "@/modules/InvoiceExitGuard/service";

export default {
  name: "WarehouseExitList",
  data: () => ({
    loading: false,
    view: "pending",
    search: "",
    startMenu: false,
    endMenu: false,
    today: new Date().toLocaleDateString("en-CA"),
    historyStart: new Date(Date.now() - (9 * 24 * 60 * 60 * 1000)).toLocaleDateString("en-CA"),
    historyEnd: new Date().toLocaleDateString("en-CA"),
    dashboard: {},
    pendingHeaders: [
      { text: "INVOICE", value: "localId" },
      { text: "CUSTOMER", value: "Customer.name" },
      { text: "PRINTED TOTAL", value: "exitPrintedTotal", align: "right" },
      { text: "CURRENT TOTAL", value: "total", align: "right" },
      { text: "PRINTED", value: "exitPrintedAt" },
      { text: "STATUS", value: "status", sortable: false },
      { text: "", value: "actions", sortable: false, align: "right" },
    ],
    historyHeaders: [
      { text: "INVOICE", value: "Invoice.localId" },
      { text: "CUSTOMER", value: "Invoice.Customer.name" },
      { text: "GUARD", value: "InvoiceExitGuard.name" },
      { text: "AMOUNT", value: "Invoice.total", align: "right" },
      { text: "RESULT", value: "result" },
      { text: "SCANNED", value: "scannedAt" },
    ],
  }),
  watch: {
    view() {
      this.search = "";
    },
  },
  created() {
    document.title = "smplsale - Warehouse Exit List";
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const response = await service.dashboard({ start: this.historyStart, end: this.historyEnd });
        this.dashboard = response.data || response || {};
      } catch (error) {
        this.$swal("Warehouse Exit", (error.data && error.data.message) || "Unable to load warehouse exit activity.", "error");
      } finally {
        this.loading = false;
      }
    },
    money(value) {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value || 0);
    },
    dateTime(value) {
      return value ? new Date(value).toLocaleString() : "—";
    },
    amountChanged(invoice) {
      return Number(invoice.total).toFixed(2) !== Number(invoice.exitPrintedTotal).toFixed(2);
    },
    async cancelExit(invoice) {
      const confirmation = await this.$swal({
        title: `Remove invoice #${invoice.localId}?`,
        text: "Use this only when the invoice was printed by mistake or the paper copy was discarded. Its QR will no longer be accepted at the door.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Remove from Exit Queue",
      });
      if (!confirmation.value) return;

      try {
        await service.cancelExitPrint(invoice.id);
        await this.load();
        this.$swal({
          toast: true,
          position: "top-end",
          timer: 2200,
          showConfirmButton: false,
          icon: "success",
          title: "Invoice removed from exit queue",
        });
      } catch (error) {
        this.$swal("Warehouse Exit", (error.data && error.data.message) || "Unable to remove this invoice from the exit queue.", "error");
      }
    },
    resultLabel(result) {
      return { approved: "Cleared", amount_mismatch: "Amount Mismatch", already_delivered: "Already Cleared" }[result] || result;
    },
    resultColor(result) {
      return result === "amount_mismatch" ? "red lighten-5" : result === "approved" ? "green lighten-5" : "blue lighten-5";
    },
    resultTextColor(result) {
      return result === "amount_mismatch" ? "red darken-3" : result === "approved" ? "green darken-3" : "blue darken-3";
    },
  },
};
</script>

<style scoped>
.warehouse-exit-list-page{background:#f8fafc}.warehouse-exit-list-card{border-radius:20px;background:#fff}.warehouse-exit-header{padding:16px;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap}.warehouse-exit-title-wrap{display:flex;align-items:center}.page-title{font-size:22px;font-weight:800;color:#0f172a}.page-subtitle{font-size:13px;color:#64748b;margin-top:2px}.warehouse-exit-filter-bar{padding:12px 16px;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;gap:12px;flex-wrap:wrap}.warehouse-exit-search{max-width:340px;min-width:260px}.history-date-filter{display:flex;align-items:center;gap:8px}.history-date-filter .v-input{width:150px}.view-toggle{border-radius:10px;overflow:hidden}.summary-row{display:grid;grid-template-columns:repeat(3,minmax(0,220px));gap:12px}.summary-box{background:#f8fafc;border-radius:16px;padding:14px}.summary-label{font-size:12px;color:#64748b}.summary-value{font-size:22px;font-weight:800;color:#0f172a;margin-top:4px}.list-section-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}.section-title{font-size:16px;font-weight:800;color:#0f172a}.section-subtitle{font-size:12px;color:#64748b;margin-top:2px}.warehouse-exit-table{border:1px solid #e2e8f0;border-radius:16px;overflow:hidden}.warehouse-exit-row:hover{background:#f8fafc}.empty-state{padding:38px 20px;text-align:center;color:#64748b}.empty-title{margin-top:8px;font-size:16px;font-weight:800;color:#334155}.empty-subtitle{font-size:12px;margin-top:3px}@media(max-width:700px){.warehouse-exit-search{max-width:100%;min-width:100%}.history-date-filter{width:100%;flex-wrap:wrap}.history-date-filter .v-input{flex:1;min-width:130px}.view-toggle{width:100%}.view-toggle .v-btn{flex:1}.summary-row{grid-template-columns:1fr}.warehouse-exit-header{align-items:flex-start}.warehouse-exit-title-wrap{align-items:flex-start}}
</style>
